import { merge } from 'lodash-es';
import legacyDe from '@meitu/x-design-locales-legacy/chunks/saasEditor/de.json';
import legacyEn from '@meitu/x-design-locales-legacy/chunks/saasEditor/en.json';
import legacyEs from '@meitu/x-design-locales-legacy/chunks/saasEditor/es.json';
import legacyId from '@meitu/x-design-locales-legacy/chunks/saasEditor/id.json';
import legacyPt from '@meitu/x-design-locales-legacy/chunks/saasEditor/pt.json';
import legacyTh from '@meitu/x-design-locales-legacy/chunks/saasEditor/th.json';
import legacyZhHans from '@meitu/x-design-locales-legacy/chunks/saasEditor/zh-Hans.json';
import legacyZhHant from '@meitu/x-design-locales-legacy/chunks/saasEditor/zh-Hant.json';
import type { SaasEditorLocaleText } from '@meitu/x-design-locales-legacy/types';
import nextDe from '@meitu/x-design-locales-next/de.json';
import nextEn from '@meitu/x-design-locales-next/en.json';
import nextEs from '@meitu/x-design-locales-next/es.json';
import nextId from '@meitu/x-design-locales-next/id.json';
import nextPt from '@meitu/x-design-locales-next/pt.json';
import nextTh from '@meitu/x-design-locales-next/th.json';
import type { NextLocaleText } from '@meitu/x-design-locales-next/types';
import nextZhHans from '@meitu/x-design-locales-next/zh-Hans.json';
import nextZhHant from '@meitu/x-design-locales-next/zh-Hant.json';
import {
    createLocaleTextWithDevI18nKey,
    getLocale,
    setLocale,
    useLocaleText,
    type LocaleTextPrinter,
} from '@meitu/xdesign-i18n';

export type LocaleText = NextLocaleText & SaasEditorLocaleText;

const zhHansMerged = merge({}, nextZhHans, legacyZhHans);

/** 内置文案：每种展示语言仅合并同名的 next 扁平表 + legacy saasEditor chunk，不跨语言兜底合并。 */
export const localeTextMap: Record<string, LocaleText> = {
    en: merge({}, nextEn, legacyEn),
    'zh-Hans': zhHansMerged,
    de: merge({}, nextDe, legacyDe),
    es: merge({}, nextEs, legacyEs),
    id: merge({}, nextId, legacyId),
    pt: merge({}, nextPt, legacyPt),
    th: merge({}, nextTh, legacyTh),
    'zh-Hant': merge({}, nextZhHant, legacyZhHant),
};

export type LocaleCode = keyof typeof localeTextMap;

export const SUPPORT_LOCALES = Object.keys(localeTextMap);

function mergeLocaleText(
    locale: string,
    fallback: LocaleText,
    nextLocaleMap: Record<string, unknown>,
    devNextOverrides: Record<string, unknown>,
    legacyLocaleMap: Record<string, unknown>,
) {
    localeTextMap[locale] = merge({}, fallback, nextLocaleMap, devNextOverrides, legacyLocaleMap) as LocaleText;
}

// 浏览器环境外部注入多语言
if (typeof window !== 'undefined' && window.__LOCALE__) {
    const locale = window.__LOCALE__;
    const fallback = localeTextMap[locale] ?? localeTextMap['zh-Hans'];
    const nextLocaleMap = window.__LOCALE_TEXT_NEXT_MAP__ ?? {};
    const devNextOverrides = window.__DEV_NEXT_LOCALE_OVERRIDES__ ?? {};
    const legacyLocaleMap = (window.__LOCALE_TEXT_LEGACY_MAP__?.saasEditor ?? {}) as Record<string, unknown>;

    mergeLocaleText(locale, fallback, nextLocaleMap, devNextOverrides, legacyLocaleMap);
} else if (typeof globalThis !== 'undefined') {
    const localeGlobal = globalThis as {
        __FULL_LOCALE_TEXT_MAP__?: Record<string, LocaleText>;
        __DEV_NEXT_LOCALE_OVERRIDES__?: Record<string, unknown>;
    };
    const fullLocaleText = localeGlobal.__FULL_LOCALE_TEXT_MAP__;
    if (fullLocaleText) {
        const devNextOverrides = localeGlobal.__DEV_NEXT_LOCALE_OVERRIDES__ ?? {};
        for (const loc of Object.keys(fullLocaleText)) {
            mergeLocaleText(loc, fullLocaleText[loc]!, {}, devNextOverrides, {});
        }
    }
}

type LocaleStore = {
    locale: LocaleCode;
    $t: LocaleTextPrinter<LocaleText>;
};

const localStoreCache: Partial<Record<LocaleCode, LocaleStore>> = {};

export const initLocaleStore = (locale: LocaleCode) => {
    setLocale(locale);
};

export const getLocaleStore = (_locale?: LocaleCode) => {
    const locale = (_locale || getLocale() || 'zh-Hans') as LocaleCode;
    const localeText = (localeTextMap[locale] ?? localeTextMap['zh-Hans']) as unknown as Record<string, unknown>;

    if (!localStoreCache[locale]) {
        localStoreCache[locale] = {
            locale,
            $t: createLocaleTextWithDevI18nKey(localeText, 'saasEditor'),
        } as unknown as LocaleStore;
    }

    return localStoreCache[locale]!;
};

export const useLocaleStore = () => {
    const locale = useLocaleText<LocaleCode>();

    return getLocaleStore(locale);
};
